<template>
  <DiagnoseResultLayout>
    <div class="space-y-6">
      <!-- 生成中状态 -->
      <div
        v-if="isGeneratingReport"
        class="flex justify-center py-8 animate-fade-in"
      >
        <div class="flex items-center gap-3 text-slate-500">
          <div class="w-5 h-5 border-2 border-[#F2C94C] border-t-transparent rounded-full animate-spin" />
          <span>AI 正在深度分析...</span>
        </div>
      </div>

      <!-- 消息列表 -->
      <div
        v-for="(message, index) in messages"
        :key="message.id"
      >
        <!-- 时间分割线 -->
        <div
          v-if="shouldShowTimestamp(message, index) && index > 0"
          class="flex items-center justify-center my-6"
        >
          <div class="h-px bg-slate-200 flex-1" />
          <span class="mx-4 text-xs text-slate-400">
            {{ formatTime(message.timestamp) }}
          </span>
          <div class="h-px bg-slate-200 flex-1" />
        </div>

        <!-- 消息气泡 -->
        <div
          class="flex gap-4 animate-slide-up"
          :class="{ 'flex-row-reverse': message.role === 'user' }"
        >
          <!-- 头像 -->
          <div
            class="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0"
            :class="message.role === 'ai'
              ? 'bg-[#F2C94C] text-[#1B3C59]'
              : 'bg-[#1B3C59] text-white'"
          >
            <IconBot v-if="message.role === 'ai'" :size="20" />
            <IconUser v-else :size="20" />
          </div>

          <!-- 消息内容 -->
          <div
            class="max-w-[85%]"
            :class="message.role === 'user' ? 'items-end' : 'items-start'"
          >
            <div
              class="rounded-2xl px-5 py-4"
              :class="message.role === 'ai'
                ? 'bg-white border border-slate-200 shadow-sm'
                : 'bg-[#1B3C59] text-white'"
            >
              <div class="prose prose-sm max-w-none">
                <p
                  v-for="(line, i) in message.content.split('\n')"
                  :key="i"
                  :class="{ 'ml-4': line.startsWith('•') || /^\d\./.test(line) }"
                >
                  {{ line }}
                </p>
              </div>
            </div>

            <!-- AI 消息的操作栏 -->
            <div
              v-if="message.role === 'ai'"
              class="flex items-center gap-2 mt-2"
            >
              <!-- 复制按钮 -->
              <button
                class="p-1.5 text-slate-400 hover:text-[#F2C94C] hover:bg-slate-100 rounded transition-colors"
                title="复制"
                @click="handleCopy(message.content)"
              >
                <IconCopy :size="16" />
              </button>

              <!-- 点赞按钮 -->
              <button
                class="p-1.5 rounded transition-colors"
                :class="message.feedback === 'like'
                  ? 'text-[#F2C94C] bg-[#F2C94C]/10'
                  : 'text-slate-400 hover:text-[#F2C94C] hover:bg-slate-100'"
                title="有帮助"
                @click="handleLike(message.id)"
              >
                <IconThumbsUp :size="16" />
              </button>

              <!-- 点踩按钮 -->
              <button
                class="p-1.5 rounded transition-colors"
                :class="message.feedback === 'dislike'
                  ? 'text-red-500 bg-red-50'
                  : 'text-slate-400 hover:text-red-500 hover:bg-slate-100'"
                title="没有帮助"
                @click="handleDislike(message.id)"
              >
                <IconThumbsDown :size="16" />
              </button>

              <!-- 时间戳 -->
              <span
                v-if="shouldShowTimestamp(message, index)"
                class="text-xs text-slate-400 ml-auto"
              >
                {{ formatTime(message.timestamp) }}
              </span>
            </div>

            <!-- 点踩反馈表单 -->
            <div
              v-if="message.showFeedbackForm"
              class="mt-3 p-4 bg-slate-50 rounded-xl border border-slate-200 animate-slide-down"
            >
              <div class="flex items-center justify-between mb-3">
                <span class="text-sm font-medium text-slate-700">请告诉我们原因：</span>
                <button
                  class="text-slate-400 hover:text-slate-600"
                  @click="closeFeedbackForm(message.id)"
                >
                  <IconX :size="16" />
                </button>
              </div>
              <div class="space-y-2 mb-3">
                <label
                  v-for="option in dislikeOptions"
                  :key="option.id"
                  class="flex items-center gap-2 p-2 hover:bg-white rounded cursor-pointer transition-colors"
                >
                  <input
                    v-model="feedbackSelections[message.id]"
                    type="radio"
                    :name="'feedback-' + message.id"
                    :value="option.id"
                    class="text-[#F2C94C] focus:ring-[#F2C94C]"
                  />
                  <span class="text-sm text-slate-600">{{ option.label }}</span>
                </label>
              </div>
              <textarea
                v-model="feedbackDetails[message.id]"
                placeholder="其他建议（可选）..."
                class="w-full px-3 py-2 text-sm bg-white border border-slate-200 rounded-lg resize-none focus:ring-2 focus:ring-[#F2C94C] focus:border-transparent"
                rows="2"
              />
              <button
                class="mt-3 px-4 py-2 bg-[#F2C94C] text-white text-sm font-medium rounded-lg hover:bg-[#E5B73B] transition-colors"
                @click="handleFeedbackSubmit(message.id)"
              >
                提交反馈
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- 正在输入指示器 -->
      <div
        v-if="isTyping"
        class="flex gap-4 animate-fade-in"
      >
        <div class="w-8 h-8 rounded-full bg-[#F2C94C] flex items-center justify-center">
          <IconBot :size="20" class="text-[#1B3C59]" />
        </div>
        <div class="bg-white border border-slate-200 rounded-2xl px-5 py-4 shadow-sm">
          <div class="flex gap-1">
            <span
              class="w-2 h-2 bg-slate-300 rounded-full animate-bounce"
              style="animation-delay: 0ms"
            />
            <span
              class="w-2 h-2 bg-slate-300 rounded-full animate-bounce"
              style="animation-delay: 150ms"
            />
            <span
              class="w-2 h-2 bg-slate-300 rounded-full animate-bounce"
              style="animation-delay: 300ms"
            />
          </div>
        </div>
      </div>

      <!-- 滚动锚点 -->
      <div ref="messagesEndRef" />
    </div>

    <!-- 输入区插槽 -->
    <template #inputArea>
      <div class="max-w-3xl mx-auto">
        <!-- 快捷追问 - 横向滚动 -->
        <div
          v-if="unusedQuickQuestions.length > 0"
          class="flex gap-2 mb-3 overflow-x-auto pb-2 scrollbar-hide"
        >
          <button
            v-for="q in unusedQuickQuestions"
            :key="q.id"
            class="px-4 py-2 bg-slate-100 text-slate-600 text-sm rounded-full hover:bg-[#F2C94C] hover:text-[#1B3C59] transition-colors whitespace-nowrap flex-shrink-0"
            @click="handleQuickQuestion(q.id, q.text)"
          >
            {{ q.text }}
          </button>
        </div>

        <!-- 输入框 -->
        <div class="flex gap-3">
          <div class="flex-1 relative">
            <input
              v-model="inputValue"
              type="text"
              placeholder="追问更多细节..."
              class="w-full px-5 py-3 bg-slate-100 border-0 rounded-full focus:ring-2 focus:ring-[#F2C94C] focus:bg-white transition-all"
              @keydown.enter="handleSend()"
            />
          </div>
          <button
            :disabled="!inputValue.trim() || isTyping"
            class="px-6 py-3 bg-[#F2C94C] text-[#1B3C59] font-bold rounded-full hover:bg-[#E5B73B] disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
            @click="handleSend()"
          >
            <IconSend :size="16" />
            发送
          </button>
        </div>
      </div>
    </template>
  </DiagnoseResultLayout>
</template>

<script>
import { generateManagementFeedback } from '../../services/ai-service'
import DiagnoseResultLayout from './DiagnoseResultLayout.vue'
import {
  IconBot,
  IconUser,
  IconSend,
  IconCopy,
  IconThumbsUp,
  IconThumbsDown,
  IconX,
} from '../icons'

// 点踩反馈选项
const DISLIKE_OPTIONS = [
  { id: 'irrelevant', label: '内容与问题无关' },
  { id: 'unclear', label: '表述不够清晰' },
  { id: 'unhelpful', label: '对解决问题没有帮助' },
  { id: 'incorrect', label: '存在事实性错误' },
]

export default {
  name: 'DiagnoseResultView',

  components: {
    DiagnoseResultLayout,
    IconBot,
    IconUser,
    IconSend,
    IconCopy,
    IconThumbsUp,
    IconThumbsDown,
    IconX,
  },

  props: {
    query: {
      type: String,
      default: '',
    },
    answers: {
      type: Object,
      default: () => ({}),
    },
    onBack: {
      type: Function,
      default: null,
    },
  },

  data() {
    return {
      inputValue: '',
      messages: [],
      isTyping: false,
      isGeneratingReport: true,
      quickQuestions: [
        { id: '1', text: '具体该如何操作？', used: false },
        { id: '2', text: '有成功案例吗？', used: false },
        { id: '3', text: '需要注意什么？', used: false },
      ],
      feedbackSelections: {},
      feedbackDetails: {},
      dislikeOptions: DISLIKE_OPTIONS,
    }
  },

  computed: {
    unusedQuickQuestions() {
      return this.quickQuestions.filter(q => !q.used)
    },
    context() {
      return this.$store.state.context
    },
  },

  watch: {
    messages() {
      this.$nextTick(() => {
        this.scrollToBottom()
      })
    },
  },

  mounted() {
    this.generateInitialReport()
  },

  methods: {
    scrollToBottom() {
      this.$refs.messagesEndRef?.scrollIntoView({ behavior: 'smooth' })
    },

    formatTime(timestamp) {
      return new Date(timestamp).toLocaleTimeString([], {
        hour: '2-digit',
        minute: '2-digit',
      })
    },

    shouldShowTimestamp(message, index) {
      const prevMessage = this.messages[index - 1]
      if (!prevMessage) return true
      if (index === this.messages.length - 1) return true
      return message.timestamp - prevMessage.timestamp > 5 * 60 * 1000
    },

    async generateInitialReport() {
      this.isGeneratingReport = true

      try {
        const diagnosticContext = {
          intentStage: '初步诊断',
          riskAssessment: '基于用户描述的管理困境',
          interventionProgress: '刚刚开始',
          details: Object.entries(this.answers)
            .map(([k, v]) => `${k}: ${v}`)
            .join(', '),
          teamContext: this.context,
          questionSet: 'general',
        }

        const response = await generateManagementFeedback(
          this.query || '核心骨干疑似离职，如何提前切入',
          this.context,
          diagnosticContext
        )

        const initialMessage = {
          id: 'initial',
          role: 'ai',
          content: response,
          timestamp: Date.now(),
          feedback: null,
          showFeedbackForm: false,
        }

        this.messages = [initialMessage]
      } catch (error) {
        console.error('生成诊断报告失败:', error)
        const fallbackMessage = {
          id: 'initial',
          role: 'ai',
          content: `📋 **基于您的回答，我为您生成了以下诊断报告：**

**您描述的问题：**${this.query || '核心骨干疑似离职，如何提前切入'}

**🔍 核心洞察：**
• 目标传递存在"衰减"，下属接收到的信息不完整
• 缺乏即时反馈机制，做的好与不好都一样
• 激励手段单一，难以调动不同动力类型的员工

**💡 建议方向：**
1. 建立「日清看板」机制，可视化目标进度
2. 设计即时反馈系统，让好的行为被看见
3. 针对不同员工类型，设计差异化激励

想了解更多细节，可以直接问我 👇`,
          timestamp: Date.now(),
          feedback: null,
          showFeedbackForm: false,
        }
        this.messages = [fallbackMessage]
      } finally {
        this.isGeneratingReport = false
      }
    },

    async handleSend(content = this.inputValue) {
      if (!content.trim()) return

      const userMessage = {
        id: Date.now().toString(),
        role: 'user',
        content,
        timestamp: Date.now(),
        feedback: null,
        showFeedbackForm: false,
      }
      this.messages.push(userMessage)
      this.inputValue = ''
      this.isTyping = true

      try {
        const history = this.messages.map(m => ({
          role: m.role,
          content: m.content,
        }))

        const response = await generateManagementFeedback(
          content,
          this.context,
          null,
          history
        )

        const aiMessage = {
          id: (Date.now() + 1).toString(),
          role: 'ai',
          content: response,
          timestamp: Date.now(),
          feedback: null,
          showFeedbackForm: false,
        }
        this.messages.push(aiMessage)
      } catch (error) {
        const errorMessage = {
          id: (Date.now() + 1).toString(),
          role: 'ai',
          content: '抱歉，AI 服务暂时不可用，请稍后再试。',
          timestamp: Date.now(),
          feedback: null,
          showFeedbackForm: false,
        }
        this.messages.push(errorMessage)
      } finally {
        this.isTyping = false
      }
    },

    handleQuickQuestion(questionId, text) {
      this.quickQuestions = this.quickQuestions.map(q =>
        q.id === questionId ? { ...q, used: true } : q
      )
      this.handleSend(text)
    },

    async handleCopy(content) {
      try {
        await navigator.clipboard.writeText(content)
      } catch (err) {
        console.error('复制失败:', err)
      }
    },

    handleLike(messageId) {
      this.messages = this.messages.map(m =>
        m.id === messageId
          ? { ...m, feedback: m.feedback === 'like' ? null : 'like' }
          : m
      )
    },

    handleDislike(messageId) {
      this.messages = this.messages.map(m =>
        m.id === messageId
          ? {
              ...m,
              feedback: m.feedback === 'dislike' ? null : 'dislike',
              showFeedbackForm: m.feedback !== 'dislike',
            }
          : { ...m, showFeedbackForm: false }
      )
    },

    handleFeedbackSubmit(messageId) {
      const reason = this.feedbackSelections[messageId]
      const detail = this.feedbackDetails[messageId]
      console.log('反馈提交:', { messageId, reason, detail })
      // TODO: 发送到后端
      this.messages = this.messages.map(m =>
        m.id === messageId ? { ...m, showFeedbackForm: false } : m
      )
    },

    closeFeedbackForm(messageId) {
      this.messages = this.messages.map(m =>
        m.id === messageId ? { ...m, showFeedbackForm: false } : m
      )
    },
  },
}
</script>

<style scoped>
.animate-fade-in {
  animation: fadeIn 0.3s ease-out;
}

.animate-slide-up {
  animation: slideUp 0.3s ease-out;
}

.animate-slide-down {
  animation: slideDown 0.3s ease-out;
}

@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

@keyframes slideUp {
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
}

@keyframes slideDown {
  from { opacity: 0; transform: translateY(-10px); }
  to { opacity: 1; transform: translateY(0); }
}

.scrollbar-hide {
  -ms-overflow-style: none;
  scrollbar-width: none;
}
.scrollbar-hide::-webkit-scrollbar {
  display: none;
}
</style>
